<template lang="pug">
transition(name="fade")
  div(v-if="hasError" class="container mx-auto mb-4")
    .alert-box(
      :class="variantClass"
      role="alert"
      aria-live="assertive"
      tabindex="-1"
      ref="box"
    )
      // icon
      .alert-body
        strong.alert-title {{ title || defaultTitle }}
        template(v-if="Array.isArray(error)")
          ul.alert-list
            li(v-for="(msg, i) in error" :key="i") {{ msg }}
        template(v-else)
          p.alert-message {{ error }}
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';

const props = defineProps({
  error: { type: [String, Array], default: '' }, // string or string[]
  title: { type: String, default: '' },
  variant: {
    type: String as () => 'danger' | 'warning' | 'info' | 'success',
    default: 'danger',
  },
  dismissible: { type: Boolean, default: true },
  autofocus: { type: Boolean, default: true },
});

defineEmits<{ (e: 'close'): void }>();

const hasError = computed(() =>
  Array.isArray(props.error) ? props.error.length > 0 : !!props.error,
);

const variantClass = computed(() => `alert-${props.variant}`);

const defaultTitle = computed(
  () =>
    ({
      danger: 'Something went wrong',
      warning: 'Please check',
      info: 'Note',
      success: 'Success',
    })[props.variant],
);

const box = ref<HTMLElement | null>(null);
onMounted(() => {
  if (props.autofocus && hasError.value) box.value?.focus();
});
</script>

<style scoped>
.alert-box {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  border: 1px solid var(--alert-border);
  background: var(--alert-bg);
  color: var(--alert-text);
  padding: 1rem 1.25rem;
  border-radius: 0.75rem;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
  position: relative;
}
.alert-box::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  border-radius: 0.75rem 0 0 0.75rem;
  background: var(--alert-accent, var(--alert-border));
}
.alert-icon {
  flex: 0 0 auto;
  margin-top: 0.1rem;
}
.alert-body {
  flex: 1 1 auto;
}
.alert-title {
  display: block;
  font-weight: 700;
  margin-bottom: 0.25rem;
}
.alert-message {
  margin: 0;
  line-height: 1.4;
}
.alert-list {
  margin: 0;
  padding-left: 1.1rem;
}
.alert-close {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: transparent;
  border: 0;
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
  color: inherit;
}

/* Variants */
.alert-danger {
  --alert-bg: #fdecee;
  --alert-border: #dc3545;
  --alert-text: #7a1020;
}
.alert-warning {
  --alert-bg: #fff4e5;
  --alert-border: #f59e0b;
  --alert-text: #7a4a00;
}
.alert-info {
  --alert-bg: #eaf6ff;
  --alert-border: #0d6efd;
  --alert-text: #063a7a;
}
.alert-success {
  --alert-bg: #ecfdf5;
  --alert-border: #10b981;
  --alert-text: #0f5132;
}

/* Fade */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
